<template>
  <form @submit.prevent="handleSubmit" class="grid grid-cols-2 gap-5">
    <!-- Name & Description fields for each language -->
    <div v-for="(lang, i) in arrayLang" class="flex flex-col gap-5" :key="i">
      <div>
        <label class="block text-sm font-medium text-gray-700">
          {{ translate("Name - _LANG_", { _LANG_: translate(lang) })
          }}<span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          v-model="category.name[lang]"
          :placeholder="
            translate('Enter Name.. - _LANG_', { _LANG_: translate(lang) })
          "
          class="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          required
        />
        <p
          v-if="showValidationErrors && validationErrors.name[lang]"
          class="text-red-500 text-xs mt-1"
        >
          {{ translate("This field is required") }}
        </p>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700">
          {{ translate("Description - _LANG_", { _LANG_: translate(lang) }) }}
        </label>
        <textarea
          type="text"
          v-model="category.description[lang]"
          :placeholder="
            translate('Enter Description.. - _LANG_', {
              _LANG_: translate(lang),
            })
          "
          class="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>
    </div>

    <!-- Display Order -->
    <div class="space-y-1">
      <label class="block text-sm font-medium text-gray-700">
        {{ translate("Display Order") }}<span class="text-red-500">*</span>
      </label>
      <input
        type="number"
        v-model="category.displayOrder"
        :placeholder="translate('Enter display order')"
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        required
      />
      <p
        v-if="showValidationErrors && validationErrors.displayOrder"
        class="text-red-500 text-xs mt-1"
      >
        {{ "Invalid" }}
      </p>
    </div>

    <!-- POS ID -->
    <div class="space-y-1">
      <label class="block text-sm font-medium text-gray-700">
        {{ translate("POS ID") }}
      </label>
      <input
        type="text"
        v-model="category.posId"
        :placeholder="translate('Item code from POS')"
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      />
      <p
        v-if="showValidationErrors && validationErrors.posId"
        class="text-red-500 text-xs mt-1"
      >
        {{ "Invalid" }}
      </p>
    </div>

    <!-- Category type -->
    <div class="space-y-1">
      <label class="block text-sm font-medium text-gray-700">
        {{ translate("Type") }}<span class="text-red-500">*</span>
      </label>
      <select
        v-model="category.type"
        class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        required
      >
        <option v-for="(type, i) in Types" :key="i" :value="type.value">
          {{ type.text }}
        </option>
      </select>
      <h2 class="text-gray-500 font-semibold text-sm">Recommended size: 306 x 306</h2>
    </div>

    <!-- URL or IMAGE -->
    <div class="space-y-1">
      <label class="block text-sm font-medium text-gray-700">
        {{ category.type === "url" ? translate("URL") : translate("IMAGE")
        }}<span class="text-red-500">*</span>
      </label>

      <!-- URL Input for Link type -->
      <input
        v-if="category.type === 'url'"
        type="url"
        v-model="category.url"
        :placeholder="translate('Enter URL (e.g., https://example.com)')"
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        required
      />

      <div class="flex flex-col gap-2" v-if="category.type === 'image'">
        <V2BaseFileInput id="category.upate.imageuploader" v-model="uploadedFile" :error-message="validationErrors.fileError"
          :max-size-m-b="2" accept="image/*" @error="validationErrors.fileError = $event" @input="validationErrors.fileError = ''"
          :placeholder="translate('Click or drag an image here to upload')" :disabled="isSubmitting" />
      </div>

      <p
        v-if="showValidationErrors && validationErrors.url"
        class="text-red-500 text-xs mt-1"
      >
        {{ getUrlErrorMessage() }}
      </p>
    </div>

    <div>
      <label class="block text-sm font-medium text-gray-700">
        {{ translate("Enabled?") }}<span class="text-red-500">*</span>
      </label>
      <BaseSwitch v-model="category.enabled"/>
    </div>

    <div class="grid grid-gap-5" v-if="false">
      <BaseSelectWithLoadMore
        v-model="selectedLocations"
        :get-url="'locations'"
        :per-page="10"
        value-key="id"
        label-key="attributes.name"
        :placeholder="translate('Select Locations')"
        multiple
      />

      <BaseSelectWithLoadMore
        v-model="selectedApplications"
        :get-url="'applications'"
        :per-page="10"
        value-key="id"
        label-key="attributes.label"
        :placeholder="translate('Select Application')"
        multiple
      />
    </div>

    <!-- Submit button -->
    <div class="mt-5 sm:mt-6 text-center col-span-2">
      <button
        :id="'categories.updatecategory'"
        type="submit"
        class="inline-flex justify-center w-1/2 rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
        :class="{
          'opacity-50 cursor-not-allowed': isSubmitting || !isFormValid,
        }"
        :disabled="isSubmitting || !isFormValid"
      >
        <i v-if="isSubmitting" class="fas fa-spinner fa-spin mr-2"></i>
        {{ submitBtnText }}
      </button>
    </div>
  </form>
</template>

<script>
import BaseSwitch from '@/components/BaseSwitch.vue';
import BaseSelectWithLoadMore from '@/components/V2/BaseSelectWithLoadMore/BaseSelectWithLoadMore.vue';
import V2BaseFileInput from '@/components/V2/V2BaseFileInput.vue';
import { notificationAlerts, translations } from '@/mixins';
import { updateSpecificCategory, createNewCategory } from '@/services/V2/menus/categories.service';

export default {
  name: 'CategoryDetailsUpdateCategory',
  components: {
    V2BaseFileInput,
    BaseSwitch,
    BaseSelectWithLoadMore,
  },

  mixins: [translations, notificationAlerts],

  props: {
    categoryDetails: {
      type: Object,
      default: null,
    },
  },

  data () {
    return {
      category: {
        url: '',
        name: {},
        description: {},
        displayOrder: 1,
        posId: '',
        enabled: true,
        type: 'url',
        ...this.categoryDetails,
      },
      arrayLang: [],
      Types: [
        { text: 'Upload Image', value: 'image' },
        { text: 'Image URL', value: 'url' },
      ],
      isSubmitting: false,
      validationErrors: {
        name: {},
        type: false,
        url: false,
        fileError: '',
      },
      uploadedFile: null,
      showValidationErrors: false,
      selectedLocations: [],
      selectedApplications: [],
    };
  },

  computed: {
    isFormValid () {
      const hasNameErrors = this.arrayLang.some(
        (lang) => !this.category.name[lang],
      );
      const hasTypeError = !this.category.type;

      // For category type, check if content is not just empty HTML tags
      let hasUrlError = false;
      if (this.category.type === 'url') {
        hasUrlError = !this.category.url || !this.isValidUrl(this.category.url);
      }

      return !hasNameErrors && !hasTypeError && !hasUrlError;
    },

    categoryData () {
      const formData = new FormData();
      const appendField = (key, value) => formData.append(key, value ?? '');
      for (const i in this.arrayLang) {
        appendField(`name[${this.arrayLang[i]}]`, this.category.name[this.arrayLang[i]]);
        appendField(`description[${this.arrayLang[i]}]`, this.category.description[this.arrayLang[i]]);
      }
      appendField('display-order', this.category.displayOrder);
      appendField('code', this.category.posId);
      appendField('enabled', true);
      if (this.uploadedFile) {
        appendField('image', this.uploadedFile);
      } else {
        appendField('image-uri', this.category.url);
      }
      appendField('type', this.category.type);
      return formData;
    },

    submitBtnText () {
      return this.categoryDetails ? 'Update Category' : 'Save';
    },
  },

  mounted () {
    // Get supported languages from concept settings
    if (this.getConceptSettings?.attributes?.['primary-language']) {
      this.arrayLang.push(
        this.getConceptSettings.attributes['primary-language'],
      );
    }

    if (this.getConceptSettings?.attributes?.['secondary-language']) {
      this.arrayLang.push(
        this.getConceptSettings.attributes['secondary-language'],
      );
    }
  },

  watch: {
    'category.type': {
      handler (newType) {
        this.$nextTick(() => {
          if (newType === 'image') {
            this.$set(this.category, 'url', '');
          }
        });
      },
      immediate: true,
    },
  },

  methods: {
    isValidUrl (string) {
      try {
        const url = new URL(string);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch (_) {
        return false;
      }
    },

    getUrlErrorMessage () {
      if (this.category.type === 'url') {
        if (!this.category.url) {
          return this.translate('URL is required');
        } else if (!this.isValidUrl(this.category.url)) {
          return this.translate('Please enter a valid URL (e.g., https://example.com)');
        }
      }
      return this.translate('This field is required');
    },

    resetFields () {
      this.category = {
        url: '',
        name: {},
        description: {},
        displayOrder: 1,
        posId: '',
        enabled: true,
        type: 'url',
      };

      // Re-initialize title object with empty values
      this.arrayLang.forEach((lang) => {
        this.$set(this.category.name, lang, '');
      });
    },

    async handleSubmit () {
      this.validateFields();

      if (!this.isFormValid) {
        this.showValidationErrors = true;
        return;
      }

      if (this.categoryDetails) {
        await this.updateCategory(this.categoryData);
      } else {
        await this.createCategory(this.categoryData);
      }
    },

    async createCategory (data) {
      this.isSubmitting = true;
      try {
        await createNewCategory(this.$route?.params?.menuId, data);
        this.successNotification('CATEGORY CREATED!', 'Category has been created successfully!');
        this.$emit('onSuccess');
      } catch (error) {
        this.systemErrorNotification('SYSTEM ERROR!', 'Something went wrong, please try again!');
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateCategory (data) {
      this.isSubmitting = true;
      try {
        await updateSpecificCategory(this.$route?.params?.menuId, this.$route?.params?.categoryId, data);
        this.successNotification('CATEGORY UPDATED!', 'Category has been updated successfully!');
        this.$emit('onSuccess');
      } catch (error) {
        this.systemErrorNotification('SYSTEM ERROR!', 'Something went wrong, please try again!');
      } finally {
        this.isSubmitting = false;
      }
    },

    /**
     * Validate all form fields and set error flags
     */
    validateFields () {
      // Reset validation errors
      this.validationErrors = {
        name: {},
        type: false,
        url: false,
        fileError: '',
      };

      // Check each field
      if (!this.category.type) {
        this.validationErrors.type = true;
      }
      if (this.category.type === 'url') {
        // Check if URL is not empty and is valid
        if (!this.category.url) {
          this.validationErrors.url = true;
        } else if (!this.isValidUrl(this.category.url)) {
          this.validationErrors.url = true;
        }
      }

      // Check language titles
      for (const lang of this.arrayLang) {
        if (!this.category.name[lang]) {
          this.$set(this.validationErrors.name, lang, true);
        } else {
          this.$set(this.validationErrors.name, lang, false);
        }
      }

      // Show validation errors
      this.showValidationErrors = true;
    },
  },
};
</script>
